import {
  CanvasHTMLAttributes,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle
} from 'react'
import { useResizeDetector } from 'react-resize-detector'
import SignatureCanvas from 'react-signature-canvas'

import {
  CanvasContainer,
  CanvasPlaceholderText
} from './ActivityInputDraw.styles'
import { useTranslation } from 'react-i18n-lite'

const useCanvasImageLoader = ({
  base64,
  canvasRef,
  enabled
}: {
  base64: string
  canvasRef: React.RefObject<SignatureCanvas>
  enabled?: boolean
}) => {
  useEffect(() => {
    if (!enabled || !base64) {
      return
    }

    const memoryImage = new Image()
    memoryImage.src = base64

    memoryImage.onload = () => {
      const canvas = canvasRef.current?.getCanvas()
      const context = canvas?.getContext('2d')

      if (!canvas || !context) {
        return
      }

      const originalDimension = [memoryImage.width, memoryImage.height]
      const canvasDimension = [canvas.offsetWidth, canvas.offsetHeight]

      const scaleFactor = Math.min(
        canvasDimension[0] / originalDimension[0],
        canvasDimension[1] / originalDimension[1],
        1
      )

      const [imageWidth, imageHeight] = [
        Math.floor(originalDimension[0] * scaleFactor),
        Math.floor(originalDimension[1] * scaleFactor)
      ]

      context.drawImage(
        memoryImage,
        canvas.offsetWidth / 2 - imageWidth / 2,
        canvas.offsetHeight / 2 - imageHeight / 2,
        imageWidth,
        imageHeight
      )
    }
  }, [base64, canvasRef, enabled])
}

type CanvasSignatureEditorProps = {
  base64: string
  onChange: (value: string) => void
  enabled?: boolean
}

export type CanvasSignatureEditorRef = {
  clear: () => void
}

const CanvasSignatureEditor = forwardRef<
  CanvasSignatureEditorRef,
  CanvasSignatureEditorProps
>(({ enabled, base64, onChange }, ref) => {
  const canvasRef = useRef<SignatureCanvas>(null)

  useCanvasImageLoader({ base64, canvasRef, enabled })

  const [justResized, setJustResized] = useState<boolean>()
  const { ref: canvasContainerRef } = useResizeDetector<HTMLDivElement>({
    onResize: () =>
      // `justResized` is set as `true` only when size changes, not at initilization
      setJustResized((prevJustResized) => prevJustResized === false)
  })

  const [isEmpty, setIsEmpty] = useState(!base64)

  const handleChange = (value: string) => {
    onChange(value)
    setIsEmpty(!value)
  }

  const handleClear = () => {
    canvasRef.current?.clear()
    handleChange('')
    setIsEmpty(true)
  }

  if (justResized && base64) {
    setJustResized(false)
    handleClear()
  }

  useImperativeHandle(ref, () => ({
    clear: handleClear
  }))

  const { t } = useTranslation()

  if (!enabled) {
    return null
  }

  return (
    <CanvasContainer ref={canvasContainerRef}>
      <SignatureCanvas
        onBegin={() => setJustResized(false)}
        onEnd={() => handleChange(canvasRef.current?.toDataURL() || '')}
        ref={canvasRef}
        clearOnResize={false}
        canvasProps={
          {
            'data-testid': 'signature-canvas'
          } as CanvasHTMLAttributes<HTMLCanvasElement>
        }
      />
      {isEmpty && (
        <CanvasPlaceholderText>
          {t('activity-input-draw.draw-fullname')}
        </CanvasPlaceholderText>
      )}
    </CanvasContainer>
  )
})

CanvasSignatureEditor.displayName = 'CanvasSignatureEditor'

export default CanvasSignatureEditor
